import React, { useEffect, createRef, useRef, useState, useMemo } from "react";
import { useHistory } from "react-router";
import classNames from "classnames";
import { arrowUndoOutline } from "ionicons/icons";
import { IconFlagFilled } from "@tabler/icons-react";
import {
  IonBadge,
  IonContent,
  isPlatform,
  IonList,
  IonItem,
  IonLabel,
  IonItemOptions,
  IonItemOption,
  IonItemSliding,
  IonIcon,
  IonSpinner,
} from "@ionic/react";

import { FormattedMessage } from "react-intl";

// CSS styles
import "./styles.css";

// Components
import { Message } from "../../interfaces";
import MessageContent from "./message-content";
import InteractiveActions from "./interactive-actions";
import MessageReactions from "./message-reactions";
import MediaModal from "./media-modal";
import ActionsModal from "./message-actions-modal";
import ReplyContent from "../common/reply-content";
import MessageActions from "./message-actions";
import ActionDropdown from "./message-actions-dropdown";

// API
import ChatQualityApi from "../../api/chat-quality";
import useContactStore, { useContactData } from "../../store/contact";
import { groupMessages } from "../../utils/contacts";

interface ItemSlidingCustomEvent extends CustomEvent {
  target: HTMLIonItemSlidingElement;
}

interface ILayoutProps {
  replyTo: (args: { message: Message }) => void;
  actions?: string[];
  mode?: String;
  scroll?: Number;
  scrollEscalations?: Number;
  solveEscalations?: Function;
  type?: "playground" | "conversation" | "web-chat";
  isLoading?: boolean;
  actionClick?: Function;
}

const isMobile = isPlatform("android") || isPlatform("ios");

/*
 *
 * Pending:
 * Why use ref when there is a scroll to message id (escalations)
 *
 */

const ChatDetail: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const history = useHistory();
  const contentRef = createRef<HTMLIonContentElement>();
  const bottomEl = useRef<HTMLDivElement>(null);
  const myRef = useRef<HTMLDivElement>(null);
  const {
    mode,
    scroll,
    scrollEscalations,
    solveEscalations,
    type = "conversation",
    replyTo,
    actionClick,
    actions = [],
  } = props;

  const { loading: chatLoading, messages } =
    useContactStore(state => ({ loading: state.loading, messages: state.messages }));
  const chat = useContactData();
  const messagesGroupedByDay = useMemo(() => groupMessages(messages), [messages]);

  const isEscalated = chat.contact.escalated;
  const isLoading = props.isLoading || chatLoading;

  const [isOpen, setIsOpen] = useState(false);

  const [mediaMessage, setMediaMessage] = useState<Message | null>(null);
  const [messageId, setLastMessageId] = useState<string | null>(null);
  const [contactId, setContactId] = useState<string | null>(null);

  // Actions drop down
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [dropdown, setDropdown] = useState({ visible: false, x: 0, y: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);

  //Actions modal
  const [isActionOpen, setIsActionOpen] = useState(false);
  const [actionedMessage, setActionedMessage] = useState<Message | null>(null);
  const [action, setAction] = useState<string>("");

  const slideTimeout = useRef<NodeJS.Timeout | null>(null);

  const scrollToBottom = () => {
    if (bottomEl.current) {
      bottomEl.current.scrollIntoView();
    }
  };

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const currentContactId = chat.contact.id;

    const isNewConversation = contactId !== currentContactId;
    const isNewMessage = lastMessage?.id && lastMessage.id !== messageId;

    const scroll = (behavior: ScrollBehavior = "auto") => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          bottomEl.current?.scrollIntoView({ behavior });
        });
      });
    };

    if (!isLoading) {
      scroll("auto");
    }

    if (isNewConversation) {
      console.log("New contact selected — jumping to bottom");
      scroll("auto");
    } else if (isNewMessage) {
      console.log("New message — smooth scroll");
      scroll("smooth");
    }

    setContactId(currentContactId);
    if (lastMessage?.id) {
      setLastMessageId(lastMessage.id);
    }
  }, [messages, chat.contact.id, isLoading]);

  useEffect(() => {
    if (scroll) {
      bottomEl.current?.scrollIntoView();
    }
  }, [scroll]);

  useEffect(() => {
    if (scrollEscalations) {
      myRef?.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [scrollEscalations]);

  const openMedia = (message: Message) => {
    setMediaMessage(message);
    setIsOpen(true);
  };

  const scrollToMessage = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ block: "center", behavior: "smooth" });
      element.classList.add("highlight");
      setTimeout(() => {
        element.classList.remove("highlight");
      }, 1000);
    }
  };

  const handleSlide = async (e: ItemSlidingCustomEvent, message: Message) => {
    if (slideTimeout.current) {
      clearTimeout(slideTimeout.current);
    }
    slideTimeout.current = setTimeout(async () => {
      const finalRatio = await e.target.getSlidingRatio();
      if (finalRatio <= -1) {
        await e.target.closeOpened();
        replyTo({ message });
      }
    }, 300);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdown.visible &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdown((prev) => ({ ...prev, visible: false }));
        setSelectedMessage(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef, dropdown.visible]);

  const handleShowActions = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdown({
      visible: true,
      x: rect.left,
      y: rect.bottom - 50,
    });
  };

  const handleAction = async ({
    message,
    action,
  }: {
    message: Message;
    action: string;
  }) => {
    setActionedMessage(message);
    setAction(action);

    const cloneMessage = async ({ message }: { message: Message }) => {
      const { data, err } = await ChatQualityApi.copyToPlayground({
        messageId: message.id,
      });
      if (data) {
        history.push(`/ai`);
      }
      if (err) {
        console.error("Error copying message:", err);
      }
    };

    switch (action) {
      case "reply":
        replyTo({ message });
        break;
      case "clone":
        await cloneMessage({ message });
        console.log("Action:", action);
        break;
      case "debug":
        setIsActionOpen(true);
        console.log("Action:", action);
        break;
      case "flag":
        setIsActionOpen(true);
        console.log("Action:", action);
        break;
      default:
        console.log("not defined action:", action);
    }
  };

  return (
    <IonContent ref={contentRef} scrollY={dropdown.visible ? false : true}>
      {isLoading && messages?.length <= 2 && (
        <div className="loadingMessages fs-4 sticky-top flex justify-content-center align-items-center gap-2">
          <IonSpinner name="crescent" />
          <p className="mb-0">
            <FormattedMessage id="contactDetail.loadingChat" />
          </p>
        </div>
      )}
      <IonList
        className={classNames("chat-detail-list min-h-100 flex flex-column justify-content-end", {
          playground: type === "playground",
          "web-chat": type === "web-chat",
        })}
      >
        {Object.entries(messagesGroupedByDay ?? {}).map(
          ([key, messages]) => (
            <React.Fragment key={key}>
              <IonItem lines="none">
                <IonLabel className="time-badge">
                  <IonBadge color="light">{key}</IonBadge>
                </IonLabel>
              </IonItem>
              {messages.map((message: Message) => {
                const isActive = selectedMessage?.id === message.id;
                const isHovered = hoveredMessageId === message.id;
                const shouldShowActions =
                  isActive || (!selectedMessage && isHovered);
                const renderItemContent = () => (
                  <IonItem
                    lines="none"
                    id={message.id}
                    className={classNames("", {
                      right: message.right,
                      escalated:
                        message.escalated === true &&
                        ["conversation", "playground"].includes(type),
                    })}
                  >
                    <IonLabel>
                      <div
                        {...(message.escalated ? { ref: myRef } : {})}
                        className={classNames("chat-wrapper", {
                          right: message.right,
                          escalated:
                            message.escalated === true &&
                            ["conversation", "playground"].includes(type),
                        })}
                      >
                        <div
                          className={classNames("chat-holder", {
                            interactive: message.interactive.flag === true,
                          })}
                          onMouseEnter={() => {
                            setHoveredMessageId(message?.id || null);
                          }}
                          onMouseLeave={() => {
                            setHoveredMessageId(null);
                          }}
                        >
                          {!isMobile && shouldShowActions && (
                            <MessageActions
                              //message={message}
                              //hoveredMessageId={hoveredMessageId}
                              //activeDropdownId={activeDropdownId}
                              //active={message.id === selectedMessage?.id}
                              showActions={(e) => {
                                handleShowActions(e);
                                setSelectedMessage(message);
                              }}
                            />
                          )}
                          <div className="chat-item">
                            <ReplyContent
                              message={message.replyTo}
                              right={message.right}
                              onAfterClick={(id: string) => scrollToMessage(id)}
                            />
                            <MessageContent
                              message={message}
                              mode={mode}
                              type={type}
                              openMedia={openMedia}
                              scrollToBottom={scrollToBottom}
                            />
                          </div>
                          <MessageReactions message={message} />
                          <InteractiveActions
                            message={message}
                            type={type}
                            actionClick={actionClick}
                          />
                          {message.error && (
                            <div className="chat-item-error color-step-500">
                              {message.error}
                            </div>
                          )}
                        </div>
                      </div>
                    </IonLabel>
                    {message.flag && ["conversation"].includes(type) && (
                      <div
                        slot="end"
                        className="chat-item-flag"
                        onClick={() => {
                          setIsActionOpen(true);
                          setAction("flag");
                          setActionedMessage(message);
                        }}
                      >
                        <div>
                          <IconFlagFilled size={20} />
                        </div>
                      </div>
                    )}
                  </IonItem>
                );
                return isMobile ? (
                  <IonItemSliding
                    key={message.id || "new"}
                    onIonDrag={(e: ItemSlidingCustomEvent) =>
                      handleSlide(e, message)
                    }
                  >
                    <IonItemOptions side="start">
                      <IonItemOption color="light">
                        <IonIcon slot="end" icon={arrowUndoOutline}></IonIcon>
                      </IonItemOption>
                    </IonItemOptions>

                    {renderItemContent()}
                  </IonItemSliding>
                ) : (
                  <React.Fragment key={message.id || "new"}>
                    {renderItemContent()}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          )
        )}
        {mode !== "desktop" && isEscalated && solveEscalations ? (
          <div className="escalation-button-holder">
            <button
              onClick={() => solveEscalations(contactId)}
              className="btn btn-visito-primary-border"
            >
              <FormattedMessage id="esc.solve" />
            </button>
          </div>
        ) : null}
        <div className="position-absolute bottom-0" ref={bottomEl}></div>
      </IonList>

      <MediaModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        message={mediaMessage}
      />

      {action && actionedMessage && (
        <ActionsModal
          isOpen={isActionOpen}
          setIsOpen={(bool: boolean) => {
            setIsActionOpen(bool);
            setActionedMessage(null);
            setAction("");
          }}
          action={action}
          message={actionedMessage}
          messages={messages}
        />
      )}

      {!isMobile && (
        <ActionDropdown
          ref={dropdownRef}
          actions={actions}
          dropdown={dropdown}
          selectedMessage={selectedMessage}
          onAction={({ action }: { action: string }) => {
            if (selectedMessage) {
              handleAction({
                message: selectedMessage,
                action,
              });
            }
          }}
          onClose={() => {
            setSelectedMessage(null);
            setDropdown((v) => ({ ...v, visible: false }));
          }}
        />
      )}
    </IonContent>
  );
};

export default ChatDetail;
